import React, { createContext, useCallback, useEffect } from "react";

interface Panel {
  jsx: JSX.Element
  id: string
  width: number
};

export type LayoutType = "wide" | "narrow";

interface SidePanelContextValue {
  panel: Panel | null
  width: number
  layoutType: LayoutType
  isClosing: boolean
  isOpening: boolean
  isOpen: boolean
  hasBack: boolean
  back: () => void
  openPanel: (panel: Panel) => void
  replacePanel: (panel: Panel) => void
  setWidth: (width: number) => void
  updatePanel: (jsx: JSX.Element) => void
  closePanel: () => void
}

const defaultSidePanelContext = {
  panel: null,
  width: 0,
  layoutType: "narrow" as LayoutType,
  isClosing: false,
  isOpening: false,
  isOpen: false,
  hasBack: false,
  back: () => { },
  openPanel: () => { },
  replacePanel: () => { },
  setWidth: () => { },
  updatePanel: () => { },
  closePanel: () => { }
};

export const SidePanelContext = createContext<SidePanelContextValue>(defaultSidePanelContext);

export const IsInSidePanelContext = createContext<boolean>(false);

export const SidePanelProvider = (props: React.PropsWithChildren<unknown>) => {
  const [panels, setPanels] = React.useState<Panel[]>([]);
  const [isOpening, setIsOpening] = React.useState(false);
  const [isClosing, setIsClosing] = React.useState(false);
  const [width, setWidth] = React.useState(0);
  const { children } = props;
  const panel = panels.length > 0 ? panels[panels.length - 1] : null;

  const openPanel = useCallback((p: Panel) => {
    if (isOpening || isClosing) {
      return;
    }

    setPanels((state) => [...state.filter(existingPanel => existingPanel.id !== p.id), p]);
    setIsOpening(true);
    setTimeout(() => {
      setIsOpening(false);
    }, 300);
  }, [isOpening, isClosing]);

  const replacePanel = useCallback((p: Panel) => {
    if (isOpening || isClosing) {
      return;
    }

    setPanels((state) => {
      const panelsBeforeCurrent = [...state];
      panelsBeforeCurrent.pop();

      const panelsBeforeCurrentWithoutReplacement = panelsBeforeCurrent
        .filter(existingPanel => existingPanel.id !== p.id);

      return [...panelsBeforeCurrentWithoutReplacement, p];
    });
  }, [isOpening, isClosing]);

  const updatePanel = useCallback((jsx: JSX.Element) => {
    if (panel) {
      setPanels(state => state.map((p, idx) => idx === panels.length - 1 ? { ...p, jsx } : p));
    }
  }, [panel]);

  const closePanel = useCallback(() => {
    if (isOpening || isClosing) {
      return;
    }

    if (panel) {
      setIsClosing(true);
      setTimeout(() => {
        setIsClosing(false);
        setPanels([]);
      }, 300);
    }
  }, [isOpening, isClosing, panel]);


  useEffect(() => {
    const closeOnEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closePanel();
      }
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [closePanel]);

  const back = () => {
    setPanels(state => state.filter((_, idx) => idx !== panels.length - 1));
  }

  return (
    <SidePanelContext.Provider value={{
      panel,
      width,
      layoutType: width >= 768 ? "wide" : "narrow",
      isClosing,
      isOpening,
      isOpen: !(isOpening || isClosing) && panel !== null,
      hasBack: panels.length > 1,
      back,
      openPanel,
      replacePanel,
      setWidth,
      updatePanel,
      closePanel
    }}>
      {children}
    </SidePanelContext.Provider>
  );
};
